// ./src/components/features/Logbook/LogbookEditDrawer/LogbookEditDrawer.tsx
//
// Unified edit drawer for Logbook records: Refuels, Expenses, and Checkpoints.
//
// Design:
// - Type selector (SegmentedControl) in create mode; locked badge in edit/duplicate mode
// - Shared sections: date/vehicle, location, travel, tags, comments, documents
// - Type-specific sections render conditionally based on expenseType
// - Tank level available for all three types (required for refuels, optional for others)
// - No local validators — server-side validation via ApiError
// - All form state, effects, derived values, and handlers live in useLogbookEditForm
//
// Modes:
// - "create"    — no recordId, blank form, submits via createFn
// - "edit"      — loads record by ID, submits via updateFn
// - "duplicate" — loads record by ID (same as edit), but submits via createFn;
//                 all field values carry over including attachments

import React, { useMemo } from "react";
import {
  Stack,
  SegmentedControl,
  Group,
  Badge,
  Box,
  Skeleton,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";

import { ApiError } from "@/utils/apiResultHandler";
import { Expense, ExpenseInput } from "@/utils/api/ExpenseService";

import { AppDrawer, AppIcon, CollapsibleSection } from "@/components/generic";
import { FilePickerDrawer } from "@/components/generic";
import { PlaceLocationSection } from "@/components/features";
import { EXPENSE_TYPES } from "@/config";

import { DateTimeVehicleSection } from "./sections/DateTimeVehicleSection";
import { RefuelFieldsSection } from "./sections/RefuelFieldsSection";
import { ExpenseFieldsSection } from "./sections/ExpenseFieldsSection";
import { TankLevelSection } from "./sections/TankLevelSection";
import {
  TravelSection,
  TagsSection,
  CommentsSection,
  DocumentsSection,
} from "./sections/SharedSections";

import { useLogbookEditForm } from "./useLogbookEditForm";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface LogbookEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /**
   * Drawer mode:
   * - "create"    — blank form, submits via createFn (default when no recordId)
   * - "edit"      — loads record by ID, submits via updateFn (default when recordId present)
   * - "duplicate" — loads record by ID like edit, but submits via createFn
   *
   * When omitted, mode is auto-detected: "edit" if recordId is set, "create" otherwise.
   */
  mode?: "create" | "edit" | "duplicate";

  /** Record ID to load in edit/duplicate mode (null/undefined for create mode) */
  recordId?: string | null;

  /** Last record for the selected car (to pre-fill defaults) */
  lastRecord?: Expense | null;

  /**
   * Pre-fill data for create mode (e.g. from receipt scan).
   * Applied on top of defaults when creating a new record.
   * Ignored in edit and duplicate modes.
   */
  initialData?: Partial<Expense> | null;

  /**
   * Optional override for loading a record by ID.
   * If not provided, uses ExpensesContext.get when available.
   */
  onLoad?: (id: string) => Promise<OpResult>;

  /**
   * Optional override for creating a record.
   * If not provided, uses ExpensesContext.create when available.
   */
  onCreate?: (data: ExpenseInput) => Promise<OpResult>;

  /**
   * Optional override for updating a record.
   * If not provided, uses ExpensesContext.update when available.
   */
  onUpdate?: (id: string, data: ExpenseInput) => Promise<OpResult>;

  /**
   * Called after a record is successfully created.
   * Receives the created Expense so the page can react.
   */
  onCreated?: (record: Expense) => void;

  /**
   * Called after a record is successfully updated.
   * Receives the updated Expense so the page can react.
   */
  onUpdated?: (record: Expense) => void;

  /** Whether create operation is in progress (prop-based override) */
  isCreating?: boolean;

  /** Whether update operation is in progress (prop-based override) */
  isUpdating?: boolean;

  /** Last error from operations (prop-based override) */
  lastError?: ApiError | null;

  /** Callback to clear errors (prop-based override) */
  onClearError?: () => void;

  /** Pre-selected car ID (from URL param or context) */
  initialCarId?: string | null;

  /** Default record type for create mode */
  defaultRecordType?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Type selector config
// ─────────────────────────────────────────────────────────────────────────────

const TYPE_SELECTOR_CONFIG = [
  {
    value: String(EXPENSE_TYPES.REFUEL),
    icon: "GasStation" as const,
    i18nKey: "logbook:types.refuel",
    color: "green",
  },
  {
    value: String(EXPENSE_TYPES.EXPENSE),
    icon: "Receipt" as const,
    i18nKey: "logbook:types.expense",
    color: "blue",
  },
  {
    value: String(EXPENSE_TYPES.CHECKPOINT),
    icon: "Flag" as const,
    i18nKey: "logbook:types.checkpoint",
    color: "orange",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────

const LogbookEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={28} width={120} radius="xl" />
    <Divider />
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />
    </SimpleGrid>
    <Skeleton height={60} radius="sm" />
    <Divider />
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />
    </SimpleGrid>
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <Skeleton height={60} radius="sm" />
      <Skeleton height={60} radius="sm" />
    </SimpleGrid>
    <Divider />
    <Skeleton height={40} radius="sm" />
    <Divider />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Divider />
    <Skeleton height={20} width={100} />
    <Divider />
    <Skeleton height={20} width={80} />
    <Divider />
    <Skeleton height={20} width={120} />
    <Divider />
    <Skeleton height={20} width={110} />
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const LogbookEditDrawer: React.FC<LogbookEditDrawerProps> = (props) => {
  const { opened, onClose, mode, recordId } = props;

  const { t } = useTranslation([
    "logbook",
    "refuels",
    "expenses",
    "checkpoints",
    "common",
    "validation",
    "tags",
    "tireSets",
  ]);

  const form = useLogbookEditForm(props, t);

  // ── Mode resolution (needed for rendering decisions) ─────────────────────
  const resolvedMode = mode ?? (recordId ? "edit" : "create");
  const isEditMode = resolvedMode === "edit";
  const isDuplicateMode = resolvedMode === "duplicate";

  // Type badge is shown whenever the type is locked (edit or duplicate mode).
  const showTypeBadge = isEditMode || isDuplicateMode;

  // ── Type selector options ─────────────────────────────────────────────────
  const typeOptions = useMemo(
    () =>
      TYPE_SELECTOR_CONFIG.map(({ value, icon, i18nKey }) => ({
        value,
        label: (
          <Group gap={6} justify="center" wrap="nowrap">
            <AppIcon iconName={icon} size={14} />
            <span>{t(i18nKey)}</span>
          </Group>
        ),
      })),
    [t],
  );

  // ── Drawer title ──────────────────────────────────────────────────────────
  //
  // formData is the canonical source of truth — it's populated from the loaded
  // record as soon as the reset effect fires, so there's no need to also read
  // from `record` here.
  const drawerTitle = useMemo(() => {
    const typeConfig = TYPE_SELECTOR_CONFIG.find(
      (c) => c.value === String(form.formData.expenseType),
    );
    const typeName = typeConfig ? t(typeConfig.i18nKey) : "";

    if (isEditMode) return t("logbook:form.editTitle", { type: typeName });
    if (isDuplicateMode) return t("logbook:form.duplicateTitle", { type: typeName });
    return t("logbook:form.addTitle");
  }, [isEditMode, isDuplicateMode, form.formData.expenseType, t]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={drawerTitle}
        position="right"
        size="lg"
        responsive
        mobileHeightPercent={92}
        closable={!form.isSubmitting && !form.isLoadingRecord}
        loading={form.isSubmitting}
        errors={form.formattedGenericError}
        actionType="save-cancel"
        onConfirm={form.handleSubmit}
        confirmDisabled={form.showLoadingSkeleton}
        isDirty={form.isDirty}
      >
        {form.showLoadingSkeleton ? (
          <LogbookEditDrawerSkeleton />
        ) : (
          <Stack gap="md">
            {/* ── Type Selector / Type Badge ─────────────────────────────── */}
            {!showTypeBadge ? (
              <SegmentedControl
                value={String(form.formData.expenseType)}
                onChange={form.handleTypeChange}
                data={typeOptions}
                fullWidth
                size="sm"
              />
            ) : (
              <Box>
                {(() => {
                  const cfg = TYPE_SELECTOR_CONFIG.find(
                    (c) => c.value === String(form.formData.expenseType),
                  );
                  if (!cfg) return null;
                  return (
                    <Badge
                      variant="light"
                      color={cfg.color}
                      size="lg"
                      leftSection={<AppIcon iconName={cfg.icon} size={14} />}
                    >
                      {t(cfg.i18nKey)}
                    </Badge>
                  );
                })()}
              </Box>
            )}

            {/* ── Date, Vehicle, Odometer ────────────────────────────────── */}
            <DateTimeVehicleSection
              formData={form.formData}
              onFieldChange={form.handleFieldChange}
              onDateChange={form.handleDateChange}
              onOdometerChange={form.handleOdometerChange}
              odometerUnit={form.odometerUnit}
              apiFieldErrors={form.apiFieldErrors}
            />

            {/* ── Refuel-specific fields ─────────────────────────────────── */}
            {form.isRefuel && (
              <RefuelFieldsSection
                formData={form.formData}
                onFieldChange={form.handleFieldChange}
                onTankTypeChange={form.handleTankTypeChange}
                onFuelGradeChange={form.handleFuelGradeChange}
                onVolumeUnitChange={form.handleVolumeUnitChange}
                onVolumeChange={form.handleVolumeChange}
                onTotalPriceChange={form.handleRefuelTotalPriceChange}
                onPricePerVolumeChange={form.handlePricePerVolumeChange}
                onTotalPriceInHcChange={form.handleTotalPriceInHcChange}
                priceCalcMode={form.priceCalcMode}
                onPriceCalcModeChange={form.setPriceCalcMode}
                hasAdditionalTank={form.hasAdditionalTank}
                isDifferentCurrency={form.isDifferentCurrency}
                apiFieldErrors={form.apiFieldErrors}
              />
            )}

            {/* ── Expense-specific fields ────────────────────────────────── */}
            {form.isExpense && (
              <ExpenseFieldsSection
                formData={form.formData}
                onFieldChange={form.handleFieldChange}
                onTextInputChange={form.handleTextInputChange}
                onTotalPriceChange={form.handleExpenseTotalPriceChange}
                onCostWorkChange={form.handleCostWorkChange}
                onCostPartsChange={form.handleCostPartsChange}
                onTaxChange={form.handleTaxChange}
                onFeesChange={form.handleFeesChange}
                onTotalPriceInHcChange={form.handleTotalPriceInHcChange}
                onAmountModeChange={form.handleAmountModeChange}
                onTireSetChange={form.handleTireSetChange}
                breakdownTotal={form.breakdownTotal}
                isDifferentCurrency={form.isDifferentCurrency}
                apiFieldErrors={form.apiFieldErrors}
              />
            )}

            {/* ── Tank Level (all types) ─────────────────────────────────── */}
            <TankLevelSection
              formData={form.formData}
              onTankLevelChange={form.handleTankLevelChange}
              onTankLevelUnknownChange={form.handleTankLevelUnknownChange}
              tankVolume={form.currentTankVolume}
              selectedCar={form.selectedCar}
              collapsible={!form.isRefuel}
              expanded={form.expandedSections.tankLevel}
              onToggle={() => form.toggleSection("tankLevel")}
            />

            {/* ── Location ──────────────────────────────────────────────── */}
            <CollapsibleSection
              title={t("logbook:form.sections.location", {
                defaultValue: "Location",
              })}
              alwaysExpanded
              withDivider
            >
              <PlaceLocationSection
                placeLocation={form.placeLocation}
                showPointType={false}
                showSaveToggle={resolvedMode === "create"}
                placeTypes={form.placeTypes}
                fieldErrors={form.apiFieldErrors}
                placeNameLabel={form.whereDoneLabel}
                placeNamePlaceholder={form.whereDonePlaceholder}
                addressLabel={t("logbook:form.fields.location.label", {
                  defaultValue: "Address",
                })}
                addressPlaceholder={t(
                  "logbook:form.fields.location.placeholder",
                  { defaultValue: "Enter address" },
                )}
              />
            </CollapsibleSection>

            {/* ── Travel (collapsible) ───────────────────────────────────── */}
            <TravelSection
              formData={form.formData}
              expanded={form.expandedSections.travel}
              onToggle={() => form.toggleSection("travel")}
              onFieldChange={form.handleFieldChange}
              isEditMode={isEditMode}
            />

            {/* ── Tags (collapsible) ─────────────────────────────────────── */}
            <TagsSection
              formData={form.formData}
              expanded={form.expandedSections.tags}
              onToggle={() => form.toggleSection("tags")}
              onFieldChange={form.handleFieldChange}
            />

            {/* ── Comments (collapsible) ─────────────────────────────────── */}
            <CommentsSection
              formData={form.formData}
              expanded={form.expandedSections.additional}
              onToggle={() => form.toggleSection("additional")}
              onTextInputChange={form.handleTextInputChange}
              apiFieldErrors={form.apiFieldErrors}
            />

            {/* ── Documents (collapsible) ────────────────────────────────── */}
            <DocumentsSection
              formData={form.formData}
              expanded={form.expandedSections.documents}
              onToggle={() => form.toggleSection("documents")}
              attachedFiles={form.attachedFiles}
              onRemoveFile={form.handleRemoveFile}
              onAddClick={form.handleOpenFilePicker}
              onReorderFiles={form.handleReorderFiles}
            />
          </Stack>
        )}
      </AppDrawer>

      {/* File Picker Drawer */}
      <FilePickerDrawer
        opened={form.filePickerOpened}
        onClose={form.handleCloseFilePicker}
        onConfirm={form.handleAddFiles}
        excludeFileIds={form.formData.uploadedFilesIds}
        multiple
        title={t("common:attachments.selectFiles")}
      />
    </>
  );
};